import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  writeBatch
} from "firebase/firestore";
import { db } from "../firebase/config";

export interface ExamAssignment {
  id: string;
  examId: string;
  candidateId: string;
  orgId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'expired';
  assignedAt: string;
  startTime: string | null;
  endTime: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  startedAt: string | null;
  completedAt: string | null;
  score: number | null;
  maxScore: number | null;
  timeSpent: number | null;
  createdBy: string;
  notes: string | null;
}

export interface BulkAssignmentData {
  examId: string;
  candidateIds: string[];
  orgId: string;
  createdBy: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  notes?: string;
}

class ExamAssignmentService {
  private readonly COLLECTION = "exam_assignments";

  async createAssignment(data: Omit<ExamAssignment, "id">): Promise<ExamAssignment> {
    console.log("createAssignment called with data:", data);
    try {
      const docRef = await addDoc(collection(db, this.COLLECTION), {
        ...data,
        createdAt: new Date().toISOString()
      });
      console.log("Assignment created with ID:", docRef.id);
      return { id: docRef.id, ...data };
    } catch (error: any) {
      console.error("Error creating assignment:", error);
      throw new Error(error.message || "Failed to create assignment");
    }
  }

  async bulkCreateAssignments(data: BulkAssignmentData): Promise<{ success: ExamAssignment[]; failed: any[] }> {
    console.log("bulkCreateAssignments called with data:", data);
    const success: ExamAssignment[] = [];
    const failed: any[] = [];
    const batch = writeBatch(db);

    for (const candidateId of data.candidateIds) {
      try {
        const assignmentId = doc(collection(db, this.COLLECTION)).id;
        console.log("Creating assignment for candidate:", candidateId, "with ID:", assignmentId);
        const assignment: any = {
          id: assignmentId,
          examId: data.examId,
          candidateId: candidateId,
          orgId: data.orgId,
          status: 'pending',
          assignedAt: new Date().toISOString(),
          startTime: null,
          endTime: null,
          scheduledStart: data.scheduledStart || null,
          scheduledEnd: data.scheduledEnd || null,
          startedAt: null,
          completedAt: null,
          score: null,
          maxScore: null,
          timeSpent: null,
          createdBy: data.createdBy,
          notes: data.notes || null,
          createdAt: new Date().toISOString()
        };

        const docRef = doc(db, this.COLLECTION, assignmentId);
        batch.set(docRef, assignment);
        success.push(assignment);
        console.log("Assignment added to batch for candidate:", candidateId);
      } catch (error) {
        console.error("Error creating assignment for candidate:", candidateId, error);
        failed.push({ candidateId, error });
      }
    }

    console.log("Committing batch with", success.length, "assignments");
    await batch.commit();
    console.log("Batch commit completed. Success:", success.length, "Failed:", failed.length);
    return { success, failed };
  }

  async getAssignmentsByOrg(orgId: string): Promise<ExamAssignment[]> {
    console.log("getAssignmentsByOrg called with orgId:", orgId);

    // Add validation
    if (!orgId) {
      console.warn("getAssignmentsByOrg called with empty orgId");
      return [];
    }

    try {
      const q = query(
        collection(db, this.COLLECTION),
        where("orgId", "==", orgId)
      );
      console.log("Query created for collection:", this.COLLECTION);
      const querySnapshot = await getDocs(q);
      console.log("Query snapshot size:", querySnapshot.size);
      const assignments: ExamAssignment[] = [];
      querySnapshot.forEach((doc) => {
        console.log("Processing document:", doc.id, doc.data());
        assignments.push({ id: doc.id, ...doc.data() } as ExamAssignment);
      });
      const sortedAssignments = assignments.sort((a, b) =>
        new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime()
      );
      console.log(`Returning ${sortedAssignments.length} assignments for org ${orgId}`);
      return sortedAssignments;
    } catch (error: any) {
      console.error("Error getting assignments:", error);
      return [];
    }
  }

  async getAssignmentsByCandidate(candidateId: string): Promise<ExamAssignment[]> {
    console.log("getAssignmentsByCandidate called with candidateId:", candidateId);
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where("candidateId", "==", candidateId)
      );
      const querySnapshot = await getDocs(q);
      console.log("Query snapshot size:", querySnapshot.size);
      const assignments: ExamAssignment[] = [];
      querySnapshot.forEach((doc) => {
        assignments.push({ id: doc.id, ...doc.data() } as ExamAssignment);
      });
      return assignments.sort((a, b) =>
        new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime()
      );
    } catch (error: any) {
      console.error("Error getting candidate assignments:", error);
      return [];
    }
  }

  async getAssignmentsByExam(examId: string): Promise<ExamAssignment[]> {
    console.log("getAssignmentsByExam called with examId:", examId);
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where("examId", "==", examId)
      );
      const querySnapshot = await getDocs(q);
      console.log("Query snapshot size:", querySnapshot.size);
      const assignments: ExamAssignment[] = [];
      querySnapshot.forEach((doc) => {
        assignments.push({ id: doc.id, ...doc.data() } as ExamAssignment);
      });
      return assignments.sort((a, b) =>
        new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime()
      );
    } catch (error: any) {
      console.error("Error getting exam assignments:", error);
      return [];
    }
  }

  async updateAssignment(id: string, data: Partial<ExamAssignment>): Promise<void> {
    console.log("updateAssignment called for ID:", id, "with data:", data);
    try {
      const docRef = doc(db, this.COLLECTION, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date().toISOString()
      });
      console.log("Assignment updated successfully:", id);
    } catch (error: any) {
      console.error("Error updating assignment:", error);
      throw new Error(error.message || "Failed to update assignment");
    }
  }

  async deleteAssignment(id: string): Promise<void> {
    console.log("deleteAssignment called for ID:", id);
    try {
      const docRef = doc(db, this.COLLECTION, id);
      await deleteDoc(docRef);
      console.log("Assignment deleted successfully:", id);
    } catch (error: any) {
      console.error("Error deleting assignment:", error);
      throw new Error(error.message || "Failed to delete assignment");
    }
  }

  async bulkDeleteAssignments(ids: string[]): Promise<{ success: number; failed: number }> {
    console.log("bulkDeleteAssignments called for IDs:", ids);
    let successCount = 0;
    let errorCount = 0;
    const batch = writeBatch(db);

    for (const id of ids) {
      try {
        const docRef = doc(db, this.COLLECTION, id);
        batch.delete(docRef);
        successCount++;
        console.log("Added delete to batch for ID:", id);
      } catch (error) {
        console.error("Error adding delete to batch for ID:", id, error);
        errorCount++;
      }
    }

    await batch.commit();
    console.log("Batch commit completed. Success:", successCount, "Failed:", errorCount);
    return { success: successCount, failed: errorCount };
  }

  async startExam(assignmentId: string): Promise<void> {
    console.log("startExam called for assignment:", assignmentId);
    try {
      const docRef = doc(db, this.COLLECTION, assignmentId);
      await updateDoc(docRef, {
        status: 'in_progress',
        startedAt: new Date().toISOString(),
        startTime: new Date().toISOString()
      });
      console.log("Exam started successfully:", assignmentId);
    } catch (error: any) {
      console.error("Error starting exam:", error);
      throw new Error(error.message || "Failed to start exam");
    }
  }

  async completeExam(assignmentId: string, score: number, maxScore: number, timeSpent: number): Promise<void> {
    console.log("completeExam called for assignment:", assignmentId, { score, maxScore, timeSpent });
    try {
      const docRef = doc(db, this.COLLECTION, assignmentId);
      await updateDoc(docRef, {
        status: 'completed',
        completedAt: new Date().toISOString(),
        endTime: new Date().toISOString(),
        score: score,
        maxScore: maxScore,
        timeSpent: timeSpent
      });
      console.log("Exam completed successfully:", assignmentId);
    } catch (error: any) {
      console.error("Error completing exam:", error);
      throw new Error(error.message || "Failed to complete exam");
    }
  }

  async checkExpiredAssignments(): Promise<void> {
    console.log("checkExpiredAssignments called");
    try {
      const now = new Date().toISOString();
      console.log("Current time:", now);

      const q = query(
        collection(db, this.COLLECTION),
        where("status", "==", "pending")
      );
      const querySnapshot = await getDocs(q);
      console.log("Found", querySnapshot.size, "pending assignments");

      const batch = writeBatch(db);
      let expiredCount = 0;
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log("Checking assignment:", doc.id, "scheduledEnd:", data.scheduledEnd);
        if (data.scheduledEnd && data.scheduledEnd < now) {
          console.log("Marking assignment as expired:", doc.id);
          batch.update(doc.ref, { status: 'expired' });
          expiredCount++;
        }
      });

      if (expiredCount > 0) {
        await batch.commit();
        console.log(`Marked ${expiredCount} assignments as expired`);
      } else {
        console.log("No expired assignments found");
      }
    } catch (error: any) {
      console.error("Error checking expired assignments:", error);
    }
  }

  async getAssignmentsWithDetails(orgId: string, exams: any[], users: any[]): Promise<any[]> {
    console.log("getAssignmentsWithDetails called for org:", orgId);
    const assignments = await this.getAssignmentsByOrg(orgId);
    console.log("Found assignments:", assignments.length);
    const enriched = assignments.map(assignment => ({
      ...assignment,
      exam: exams.find(e => e.id === assignment.examId),
      candidate: users.find(u => u.id === assignment.candidateId)
    }));
    console.log("Enriched assignments:", enriched.length);
    return enriched;
  }
}

export const examAssignmentService = new ExamAssignmentService();